import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso.slice(0, 16); }
}

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("overview");

  // Broadcast state
  const [bSubject, setBSubject] = useState("");
  const [bBody, setBBody] = useState("");
  const [bRole, setBRole] = useState("All");
  const [bSending, setBSending] = useState(false);
  const [broadcasts, setBroadcasts] = useState([]);

  // Wish moderation
  const [pending, setPending] = useState([]);
  const [approvedWishes, setApprovedWishes] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [instName, setInstName] = useState("");
  const [instCode, setInstCode] = useState("");
  const [instCreating, setInstCreating] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/community/stats");
      setStats(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load stats.");
    }
  }, []);

  const loadBroadcasts = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/community/broadcasts");
      setBroadcasts(data);
    } catch {}
  }, []);

  const loadWishes = useCallback(async () => {
    try {
      const [p, a] = await Promise.all([
        api.get("/admin/community/wishes?status=pending"),
        api.get("/admin/community/wishes?status=approved"),
      ]);
      setPending(p.data);
      setApprovedWishes(a.data);
    } catch {}
  }, []);

  const loadInstitutions = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/institutions");
      setInstitutions(data);
    } catch {}
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/auth"); return; }
    if (user.role !== "admin") { setError("Admin access required."); return; }
    loadStats();
    loadBroadcasts();
    loadWishes();
    loadInstitutions();
  }, [user, authLoading, navigate, loadStats, loadBroadcasts, loadWishes, loadInstitutions]);

  const exportCsv = () => {
    const token = localStorage.getItem("ts_token");
    const url = `${API}/admin/community/export.csv`;
    // Fetch as blob to preserve auth header, then download
    fetch(url, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(r.statusText); return r.blob(); })
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u;
        a.download = `founding-thoughtsmith-${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(u);
        toast.success("CSV downloaded.");
      })
      .catch(() => toast.error("Export failed."));
  };

  const sendBroadcast = async (e) => {
    e.preventDefault();
    if (!bSubject.trim() || !bBody.trim()) {
      toast.error("Subject and body are required.");
      return;
    }
    setBSending(true);
    try {
      const { data } = await api.post("/admin/community/broadcast", {
        subject: bSubject, body: bBody, role_target: bRole,
      });
      if (data.email_delivery_enabled) {
        toast.success(`Broadcast sent to ${data.sent_count}/${data.recipients_count} recipients.`);
      } else {
        toast.success(`Broadcast logged (${data.recipients_count} recipients). Email sending is OFF — connect Resend to deliver.`);
      }
      setBSubject(""); setBBody("");
      loadBroadcasts();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Broadcast failed.");
    } finally {
      setBSending(false);
    }
  };

  const moderateWish = async (memberId, action) => {
    try {
      await api.post(`/admin/community/wishes/${memberId}`, { action });
      toast.success(action === "approve" ? "Wish published." : action === "reject" ? "Wish hidden." : "Reset.");
      loadWishes();
    } catch {
      toast.error("Could not moderate.");
    }
  };

  const createInstitution = async (e) => {
    e.preventDefault();
    if (!instName.trim()) {
      toast.error("Institution name is required.");
      return;
    }
    setInstCreating(true);
    try {
      await api.post("/admin/institutions", {
        name: instName,
        patron_join_code: instCode || undefined,
        status: "active",
      });
      toast.success("Institution created.");
      setInstName("");
      setInstCode("");
      loadInstitutions();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create institution.");
    } finally {
      setInstCreating(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-forge-900 text-forge-100">
        <Header />
        <main className="max-w-2xl mx-auto px-6 py-24 text-center">
          <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-4">Restricted</div>
          <h1 className="font-serif text-3xl tracking-tight mb-3">{error}</h1>
          <Link to="/dashboard" className="inline-block mt-6 text-xs uppercase tracking-[0.25em] text-forge-100/60 hover:text-ember-400">← Back to your study</Link>
        </main>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-forge-900 text-forge-100 flex items-center justify-center">
        <div className="text-sm tracking-[0.3em] uppercase text-ember-500 animate-pulse">Gathering the ledger…</div>
      </div>
    );
  }

  const roleData = Object.entries(stats.by_role || {}).map(([role, count]) => ({ role, count }));
  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "institutions", label: "Institutions" },
    { key: "broadcasts", label: "Broadcasts" },
    { key: "moderation", label: `Moderation${pending.length ? ` · ${pending.length}` : ""}` },
  ];

  return (
    <div className="min-h-screen bg-forge-900 text-forge-100">
      <Header />
      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-16 sm:py-20">
        <div className="flex justify-between items-end mb-8 flex-wrap gap-4">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-gold-500 mb-3">Founder's Ledger</div>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight" data-testid="admin-title">The Founding ThoughtSmith™ Community</h1>
          </div>
          <button
            onClick={exportCsv}
            className="px-5 py-2.5 border border-white/15 hover:border-ember-500 hover:text-ember-400 text-sm tracking-wide rounded-sm transition-all"
            data-testid="admin-export-csv"
          >
            Export CSV
          </button>
        </div>

        {/* Email status pill */}
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 mb-10 border rounded-sm text-xs uppercase tracking-[0.25em] ${stats.email_delivery_enabled ? "border-ember-500/40 text-ember-400" : "border-white/15 text-forge-100/55"}`} data-testid="admin-email-status">
          <span className={`w-1.5 h-1.5 rounded-full ${stats.email_delivery_enabled ? "bg-ember-500 animate-ember-glow" : "bg-forge-100/40"}`}></span>
          Email delivery: {stats.email_delivery_enabled ? `On · ${stats.sender_email}` : "Off (no-op)"}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-10 border-b border-white/10" data-testid="admin-tabs">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-xs uppercase tracking-[0.25em] border-b-2 -mb-px transition-colors ${tab === t.key ? "border-ember-500 text-ember-400" : "border-transparent text-forge-100/55 hover:text-forge-100/85"}`}
              data-testid={`admin-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <>
            <div className="grid sm:grid-cols-4 gap-6 mb-14">
              <Stat label="Total Members" value={stats.total_members} testid="admin-total" />
              <Stat label="With a Wish" value={(stats.wishes || []).length} testid="admin-wishes-count" />
              <Stat label="Distinct Roles" value={Object.values(stats.by_role || {}).filter(v => v > 0).length} testid="admin-distinct-roles" />
              <Stat label="Unsubscribed" value={stats.unsubscribed_count ?? 0} testid="admin-unsubscribed" />
            </div>

            {/* Role chart */}
            <section className="mb-14">
              <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-4">Members by Role</div>
              <div className="h-72 border border-white/10 rounded-sm p-4 bg-forge-800/40" data-testid="admin-role-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={roleData} margin={{ top: 10, right: 10, bottom: 20, left: -10 }}>
                    <CartesianGrid strokeDasharray="2 4" stroke="rgba(242,239,233,0.06)" />
                    <XAxis dataKey="role" stroke="#75716D" tick={{ fontSize: 11, fill: "#A19D97" }} tickLine={false} axisLine={false} interval={0} angle={-15} textAnchor="end" height={50} />
                    <YAxis stroke="#75716D" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ background: "#181615", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "4px", color: "#F2EFE9", fontSize: 12 }}
                      labelStyle={{ color: "#D97325", textTransform: "uppercase", letterSpacing: "0.15em", fontSize: 10 }}
                      cursor={{ fill: "rgba(217,115,37,0.08)" }}
                    />
                    <Bar dataKey="count" fill="#D97325" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </section>

            <section className="mb-14">
              <div className="text-[10px] tracking-[0.4em] uppercase text-gold-500 mb-4">Recent Signups</div>
              {stats.recent_signups.length === 0 ? (
                <p className="text-sm text-forge-100/40">No members yet.</p>
              ) : (
                <ul className="divide-y divide-white/5 border-t border-b border-white/5" data-testid="admin-recent-list">
                  {stats.recent_signups.map((s) => (
                    <li key={s.email} className="py-3 flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-serif text-lg text-forge-100">{s.name}</div>
                        <div className="text-xs text-forge-100/40 truncate">{s.email}</div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-[10px] uppercase tracking-[0.25em] text-ember-500">{s.role}</span>
                        {s.has_wish && <span className="text-[10px] uppercase tracking-[0.25em] text-gold-500">wish</span>}
                        <span className="text-[10px] uppercase tracking-[0.2em] text-forge-100/35">{formatDate(s.created_at)}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {stats.top_keywords?.length > 0 && (
              <section>
                <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-4">Common Themes in Wishes</div>
                <div className="flex flex-wrap gap-2" data-testid="admin-keywords">
                  {stats.top_keywords.map((k) => (
                    <span key={k.word} className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/10 rounded-sm text-sm">
                      <span className="text-forge-100/85">{k.word}</span>
                      <span className="font-mono text-xs text-ember-400">×{k.count}</span>
                    </span>
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        {tab === "institutions" && (
          <>
            <section className="mb-14" data-testid="admin-institution-form">
              <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-3">Create Institution</div>
              <p className="text-sm text-forge-100/60 mb-6 max-w-2xl">
                Create patron access for libraries, schools, and public institutions. Patrons use the code without joining a teacher class.
              </p>
              <form onSubmit={createInstitution} className="grid md:grid-cols-[1fr_220px_auto] gap-3 max-w-4xl">
                <input
                  type="text"
                  placeholder="Flint Public Library"
                  value={instName}
                  onChange={(e) => setInstName(e.target.value)}
                  className="px-4 py-3 bg-forge-800 border border-white/10 rounded-sm text-forge-100 placeholder-forge-100/30 focus:outline-none focus:border-ember-500 transition-colors"
                  required
                  data-testid="institution-name"
                />
                <input
                  type="text"
                  placeholder="FLINTLIBRARY"
                  value={instCode}
                  onChange={(e) => setInstCode(e.target.value.toUpperCase())}
                  className="px-4 py-3 bg-forge-800 border border-white/10 rounded-sm text-forge-100 placeholder-forge-100/30 focus:outline-none focus:border-ember-500 transition-colors font-mono tracking-[0.12em]"
                  data-testid="institution-code"
                />
                <button
                  type="submit"
                  disabled={instCreating}
                  className="px-6 py-2.5 bg-ember-500 hover:bg-ember-400 disabled:opacity-60 text-forge-900 font-semibold tracking-wide rounded-sm transition-all"
                  data-testid="institution-create"
                >
                  {instCreating ? "Creating…" : "Create"}
                </button>
              </form>
            </section>

            <section>
              <div className="text-[10px] tracking-[0.4em] uppercase text-gold-500 mb-4">Institution Usage</div>
              {institutions.length === 0 ? (
                <p className="text-sm text-forge-100/40">No institutions yet.</p>
              ) : (
                <div className="overflow-x-auto border border-white/10 rounded-sm" data-testid="institution-list">
                  <table className="w-full text-sm">
                    <thead className="bg-forge-800/80 text-[10px] uppercase tracking-[0.25em] text-forge-100/45">
                      <tr>
                        <th className="text-left px-4 py-3 font-normal">Institution</th>
                        <th className="text-left px-4 py-3 font-normal">Patron Code</th>
                        <th className="text-left px-4 py-3 font-normal">Status</th>
                        <th className="text-right px-4 py-3 font-normal">Patrons</th>
                        <th className="text-right px-4 py-3 font-normal">Readings</th>
                        <th className="text-right px-4 py-3 font-normal">Reflections</th>
                        <th className="text-right px-4 py-3 font-normal">Debates</th>
                        <th className="text-right px-4 py-3 font-normal">Reports</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {institutions.map((inst) => (
                        <tr key={inst.institution_id} className="hover:bg-forge-800/40">
                          <td className="px-4 py-4">
                            <div className="font-serif text-lg text-forge-100">{inst.name}</div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-forge-100/35">{inst.institution_id}</div>
                          </td>
                          <td className="px-4 py-4 font-mono text-ember-400 tracking-[0.12em]">{inst.patron_join_code}</td>
                          <td className="px-4 py-4 text-forge-100/65 capitalize">{inst.status}</td>
                          <td className="px-4 py-4 text-right font-mono">{inst.usage?.patron_count ?? 0}</td>
                          <td className="px-4 py-4 text-right font-mono">{inst.usage?.reading_count ?? 0}</td>
                          <td className="px-4 py-4 text-right font-mono">{inst.usage?.reflection_count ?? 0}</td>
                          <td className="px-4 py-4 text-right font-mono">{inst.usage?.debate_count ?? 0}</td>
                          <td className="px-4 py-4 text-right font-mono">{inst.usage?.report_card_count ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}

        {tab === "broadcasts" && (
          <>
            <section className="mb-14" data-testid="admin-broadcast-form">
              <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-3">Compose Broadcast</div>
              <p className="text-sm text-forge-100/60 mb-6 max-w-2xl">Sends a single transactional email to members who match the role filter. Unsubscribed members are always excluded.</p>
              <form onSubmit={sendBroadcast} className="space-y-4 max-w-2xl">
                <input
                  type="text"
                  placeholder="Subject"
                  value={bSubject}
                  onChange={(e) => setBSubject(e.target.value)}
                  className="w-full px-4 py-3 bg-forge-800 border border-white/10 rounded-sm text-forge-100 placeholder-forge-100/30 focus:outline-none focus:border-ember-500 transition-colors"
                  required
                  data-testid="broadcast-subject"
                />
                <textarea
                  rows={8}
                  placeholder="Message body. Use a blank line between paragraphs."
                  value={bBody}
                  onChange={(e) => setBBody(e.target.value)}
                  className="w-full px-4 py-3 bg-forge-800 border border-white/10 rounded-sm text-forge-100 placeholder-forge-100/30 focus:outline-none focus:border-ember-500 transition-colors font-reading text-base leading-relaxed resize-y"
                  required
                  data-testid="broadcast-body"
                />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-forge-100/50 mb-2">Send to</div>
                  <select
                    value={bRole}
                    onChange={(e) => setBRole(e.target.value)}
                    className="w-full sm:w-auto px-4 py-2.5 bg-forge-800 border border-white/10 rounded-sm text-forge-100 focus:outline-none focus:border-ember-500"
                    data-testid="broadcast-role"
                  >
                    <option value="All">All Members</option>
                    {["Student","Parent","Teacher","Librarian","Professor","Homeschool Educator","Lifelong Learner","Other"].map(r => (
                      <option key={r} value={r}>{r}s</option>
                    ))}
                  </select>
                </div>
                <button
                  type="submit"
                  disabled={bSending}
                  className="px-6 py-2.5 bg-ember-500 hover:bg-ember-400 disabled:opacity-60 text-forge-900 font-semibold tracking-wide rounded-sm transition-all"
                  data-testid="broadcast-send"
                >
                  {bSending ? "Sending…" : (stats.email_delivery_enabled ? "Send Broadcast" : "Log Broadcast (delivery off)")}
                </button>
              </form>
            </section>

            <section>
              <div className="text-[10px] tracking-[0.4em] uppercase text-gold-500 mb-4">Sent Broadcasts</div>
              {broadcasts.length === 0 ? (
                <p className="text-sm text-forge-100/40">No broadcasts yet.</p>
              ) : (
                <ul className="divide-y divide-white/5 border-t border-b border-white/5" data-testid="broadcasts-list">
                  {broadcasts.map((b) => (
                    <li key={b.broadcast_id} className="py-4">
                      <div className="flex justify-between items-baseline gap-4 flex-wrap">
                        <div className="font-serif text-lg text-forge-100">{b.subject}</div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-forge-100/40">
                          {b.role_target === "All" ? "All Members" : `${b.role_target}s`} · {b.sent_count}/{b.recipients_count} delivered · {formatDate(b.sent_at)}
                        </div>
                      </div>
                      <p className="font-reading text-sm text-forge-100/65 mt-2 line-clamp-2 whitespace-pre-line">{b.body}</p>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}

        {tab === "moderation" && (
          <>
            <section className="mb-12">
              <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-3">Pending Wishes</div>
              <p className="text-sm text-forge-100/60 mb-6 max-w-2xl">Approved wishes appear anonymized on the public <Link to="/community-voice" className="text-ember-400 hover:text-ember-500 underline-offset-4 hover:underline">Community Voice</Link> page (first name + role only).</p>
              {pending.length === 0 ? (
                <p className="text-sm text-forge-100/40">Nothing waiting.</p>
              ) : (
                <ul className="space-y-4" data-testid="moderation-pending">
                  {pending.map((w) => (
                    <li key={w.member_id} className="border border-white/10 rounded-sm p-5">
                      <div className="flex justify-between items-baseline gap-4 flex-wrap mb-2">
                        <div className="text-xs uppercase tracking-[0.25em] text-gold-500">{w.role}</div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-forge-100/35">{w.name} · {formatDate(w.created_at)}</div>
                      </div>
                      <p className="font-reading text-base text-forge-100/90 leading-relaxed mb-4">"{w.wish}"</p>
                      <div className="flex gap-3">
                        <button onClick={() => moderateWish(w.member_id, "approve")} className="px-4 py-1.5 bg-ember-500 hover:bg-ember-400 text-forge-900 text-xs uppercase tracking-[0.2em] rounded-sm" data-testid={`approve-${w.member_id}`}>Approve</button>
                        <button onClick={() => moderateWish(w.member_id, "reject")} className="px-4 py-1.5 border border-white/15 hover:border-white/40 text-forge-100/70 text-xs uppercase tracking-[0.2em] rounded-sm">Hide</button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section>
              <div className="text-[10px] tracking-[0.4em] uppercase text-gold-500 mb-3">Approved · Public</div>
              {approvedWishes.length === 0 ? (
                <p className="text-sm text-forge-100/40">Nothing approved yet.</p>
              ) : (
                <ul className="space-y-3" data-testid="moderation-approved">
                  {approvedWishes.map((w) => (
                    <li key={w.member_id} className="flex justify-between items-baseline gap-4 border-l-2 border-gold-500/40 pl-4 py-1 flex-wrap">
                      <div className="min-w-0">
                        <p className="font-reading text-sm text-forge-100/85">"{w.wish}"</p>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-forge-100/40 mt-1">— {w.role}</div>
                      </div>
                      <button onClick={() => moderateWish(w.member_id, "reset")} className="text-[10px] uppercase tracking-[0.2em] text-forge-100/50 hover:text-ember-400">Unpublish</button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="border border-white/10 p-7 rounded-sm hover:border-ember-500/50 transition-colors" data-testid={testid}>
      <div className="text-[10px] tracking-[0.3em] uppercase text-forge-100/50 mb-3">{label}</div>
      <div className="font-serif text-5xl text-forge-100">{value}</div>
    </div>
  );
}
